export interface FormattingOptions {
  decimalPlaces: number;
  numberFormat: NumberFormat;
  unit: {
    symbol: string;
    custom_unit?: string;
    direction: 'left' | 'right';
    precision?: number;
  };
}
export interface FormatNumberParams {
  text: string;
  formatOptions: Partial<FormattingOptions>;
}

export enum NumberFormat {
  COMMA_DOT = 'comma_dot',
  DOT_COMMA = 'dot_comma',
  SPACE_COMMA = 'space_comma',
  SPACE_DOT = 'space_dot',
  NO_THOUSANDS_COMMA = 'no_thousands_comma',
  NO_THOUSANDS_DOT = 'no_thousands_dot',
}

export const formatNumber = ({
  text,
  formatOptions,
}: FormatNumberParams): string => {
  const {
    numberFormat = NumberFormat.COMMA_DOT,
    unit = { symbol: '', direction: 'left' },
  } = formatOptions || {};
  const decimalPlaces = formatOptions?.decimalPlaces ?? unit?.precision ?? 3;

  const parsed = parseFloat(text);
  const isNegative = !isNaN(parsed) && parsed < 0;
  const value = Math.abs(!isNaN(parsed) ? parsed : 0);
  const roundedValue = roundToDecimalPlaces(value, decimalPlaces);
  const fixed = roundedValue.toFixed(Math.max(0, decimalPlaces));
  const [wholePart, decimalPartRaw] = fixed.split('.');

  const formattedWholePart = addThousandsSeparator(wholePart, numberFormat);
  const formattedDecimalPart =
    decimalPlaces > 0 && decimalPartRaw && !decimalPartRaw.match(/^0+$/)
      ? formatDecimalPart(decimalPartRaw, numberFormat)
      : '';

  const sign = isNegative ? '-' : '';
  const unitSymbol = getUnitSymbol(unit);

  return unit.direction === 'left'
    ? `${sign}${unitSymbol}${formattedWholePart}${formattedDecimalPart}`
    : `${sign}${formattedWholePart}${formattedDecimalPart}${unitSymbol}`;
};

const getUnitSymbol = (unit: FormattingOptions['unit']): string => {
  return unit.symbol === 'custom' ? unit.custom_unit || '' : unit.symbol || '';
};

const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.round(value * multiplier) / multiplier;
};

const addThousandsSeparator = (
  wholePart: string,
  format: NumberFormat,
): string => {
  const separator = getThousandsSeparator(format);
  if (separator === null) return wholePart;

  return wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

const getThousandsSeparator = (format: NumberFormat): string | null => {
  switch (format) {
    case NumberFormat.COMMA_DOT:
      return ',';
    case NumberFormat.DOT_COMMA:
      return '.';
    case NumberFormat.SPACE_COMMA:
    case NumberFormat.SPACE_DOT:
      return ' ';
    default:
      return null;
  }
};

const formatDecimalPart = (
  decimalPart: string | undefined,
  format: NumberFormat,
): string => {
  if (!decimalPart) return '';

  const decimalSeparator = getDecimalSeparator(format);
  return `${decimalSeparator}${decimalPart}`;
};

const getDecimalSeparator = (format: NumberFormat): string => {
  switch (format) {
    case NumberFormat.COMMA_DOT:
    case NumberFormat.SPACE_DOT:
    case NumberFormat.NO_THOUSANDS_DOT:
      return '.';
    case NumberFormat.SPACE_COMMA:
    case NumberFormat.NO_THOUSANDS_COMMA:
    case NumberFormat.DOT_COMMA:
      return ',';
    default:
      return '.';
  }
};
